import type { JSX, ReactNode } from 'react'
import { Navigate } from 'react-router'
import { useAuthStore } from '../../store/authStore.js'

interface RequirePermissionProps {
  permission: string | string[]
  children: ReactNode
  fallback?: ReactNode
}

export default function RequirePermission({
  permission,
  children,
  fallback,
}: RequirePermissionProps): JSX.Element {
  const user = useAuthStore((s) => s.user)
  const isSuperAdmin = user?.isSuperAdmin ?? false
  const isPlatformAdmin = user?.roles?.some((r) => r === 'PLATFORM_ADMIN' || r === 'PLATFORM_SUPER_ADMIN') ?? false
  const perms = Array.isArray(permission) ? permission : [permission]
  // '*' (Permission.WILDCARD in @kc3/auth) grants every permission — mirrors
  // packages/auth/src/permissions.ts's hasPermission(), which this frontend
  // package can't import directly (apps/web has no dependency on @kc3/auth).
  const hasWildcard = user?.permissions?.includes('*') ?? false
  const hasPermission =
    isSuperAdmin || isPlatformAdmin || hasWildcard || perms.some((p) => user?.permissions?.includes(p) ?? false)
  // const hasPermission = useAuthStore((s) => s.hasPermission(permission))

  if (!hasPermission) {
    if (fallback !== undefined) {
      return <>{fallback}</>
    }
    return <Navigate to="/403" replace />
  }

  return <>{children}</>
}
